import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Boxes, CheckCircle2, Clock3, CreditCard, Hammer, PackageCheck, ShoppingBag, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../../lib/api";
import { useAuth } from "../../state/auth";

type Metrics = {
  paymentsAwaitingVerification: number; ordersQueued: number; inProduction: number; ready: number;
  completed: number; salesTodayCentavos: number; salesMonthCentavos: number; averageProductionHours: number | null; overdueOrders: number;
  topProducts: Array<{ name: string; units: number; salesCentavos: number }>;
};

// Validated categorical order (dataviz skill default theme, slots 1-5): passes CVD-safety
// and contrast checks as a set. Fixed order — never cycled or reassigned by rank.
const SLICE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"];

function polarPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function wedgePath(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarPoint(cx, cy, r, startAngle);
  const end = polarPoint(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function TopProductsPie({ products }: { products: Array<{ name: string; units: number; salesCentavos: number }> }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const total = products.reduce((sum, product) => sum + product.salesCentavos, 0) || 1;
  let cursor = -90;
  const wedges = products.map((product, index) => {
    const fraction = product.salesCentavos / total;
    const startAngle = cursor;
    const endAngle = cursor + fraction * 360;
    cursor = endAngle;
    return { ...product, color: SLICE_COLORS[index], startAngle, endAngle, percent: Math.round(fraction * 1000) / 10 };
  });
  return <div className="mt-6 grid gap-8 sm:grid-cols-[auto_1fr] sm:items-center">
    <svg viewBox="0 0 200 200" width={200} height={200} className="mx-auto shrink-0" role="img" aria-label="Top products this month, by share of paid sales">
      {wedges.length === 1
        ? <circle cx={100} cy={100} r={92} fill={wedges[0].color} stroke="#fff" strokeWidth={2} />
        : wedges.map((wedge, index) => <path
            key={wedge.name}
            d={wedgePath(100, 100, 92, wedge.startAngle, wedge.endAngle)}
            fill={wedge.color}
            stroke="#fff"
            strokeWidth={2}
            opacity={hovered === null || hovered === index ? 1 : 0.45}
            className="transition-opacity duration-150"
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
          >
            <title>{`${wedge.name}: ${formatMoney(wedge.salesCentavos)} (${wedge.percent}%, ${wedge.units} sold)`}</title>
          </path>)}
    </svg>
    <ul className="grid gap-3">{wedges.map((wedge, index) => <li
      key={wedge.name}
      className={`flex items-center gap-3 rounded-xl px-2 py-1.5 transition ${hovered === index ? "bg-paper/70" : ""}`}
      onMouseEnter={() => setHovered(index)}
      onMouseLeave={() => setHovered(null)}
    >
      <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: wedge.color }} />
      <span className="min-w-0 flex-1 truncate text-sm font-bold text-ink">{wedge.name}</span>
      <span className="shrink-0 text-xs font-bold text-ink/45">{wedge.units} sold</span>
      <span className="w-14 shrink-0 text-right text-sm font-extrabold text-ink">{wedge.percent}%</span>
      <span className="w-24 shrink-0 text-right text-sm font-extrabold text-ink">{formatMoney(wedge.salesCentavos)}</span>
    </li>)}</ul>
  </div>;
}

export function AdminDashboardPage() {
  const { accessToken } = useAuth();
  const metrics = useQuery({ queryKey: ["admin","dashboard"], queryFn: () => api<Metrics>("/admin/dashboard", { token: accessToken }) });
  const topProducts = metrics.data?.topProducts ?? [];
  const cards = [
    ["Awaiting payment review", metrics.data?.paymentsAwaitingVerification ?? 0, CreditCard, "bg-butter/20 text-[#7D5B11]", "/admin/payments"],
    ["Currently queued", metrics.data?.ordersQueued ?? 0, Boxes, "bg-lavender/15 text-lavender", "/admin/queue"],
    ["In production", metrics.data?.inProduction ?? 0, Hammer, "bg-clay/10 text-clay-dark", "/admin/queue"],
    ["Overdue", metrics.data?.overdueOrders ?? 0, AlertTriangle, "bg-red-50 text-red-700", "/admin/queue?overdue=true"],
    ["Ready", metrics.data?.ready ?? 0, PackageCheck, "bg-sage/15 text-sage", "/admin/queue"],
    ["Completed", metrics.data?.completed ?? 0, CheckCircle2, "bg-ink/5 text-ink", "/admin/queue"]
  ] as const;
  return <><div><p className="text-sm font-extrabold uppercase tracking-widest text-clay-dark">Studio pulse</p><h1 className="mt-2 font-display text-4xl font-bold">Good {greeting()}.</h1><p className="mt-2 text-ink/55">Here’s what needs attention right now.</p></div>
    {metrics.isError && <div className="mt-6 rounded-2xl bg-clay/10 p-4 font-bold text-clay-dark">Dashboard data could not be loaded.</div>}
    <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{cards.map(([label,value,Icon,color,to]) => <Link to={to} key={label} className="rounded-[1.75rem] border border-ink/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-soft"><div className={`grid size-11 place-items-center rounded-2xl ${color}`}><Icon size={21} /></div><p className="mt-5 text-3xl font-extrabold">{value}</p><p className="mt-1 text-sm font-bold text-ink/50">{label}</p></Link>)}</div>
    <div className="mt-6 grid gap-4 lg:grid-cols-3"><div className="rounded-[1.75rem] bg-ink p-6 text-cream"><TrendingUp className="text-butter" /><p className="mt-6 text-sm font-bold text-cream/50">Approved sales today</p><p className="mt-1 font-display text-3xl font-bold">{formatMoney(metrics.data?.salesTodayCentavos ?? 0)}</p></div><div className="rounded-[1.75rem] bg-white p-6"><TrendingUp className="text-clay" /><p className="mt-6 text-sm font-bold text-ink/50">Approved sales this month</p><p className="mt-1 font-display text-3xl font-bold">{formatMoney(metrics.data?.salesMonthCentavos ?? 0)}</p></div><div className="rounded-[1.75rem] bg-white p-6"><Clock3 className="text-lavender" /><p className="mt-6 text-sm font-bold text-ink/50">Average production time</p><p className="mt-1 font-display text-3xl font-bold">{metrics.data?.averageProductionHours ? `${Math.round(metrics.data.averageProductionHours / 24)} days` : "Not enough data"}</p></div></div>
    <div className="mt-6 rounded-[1.75rem] border border-ink/10 bg-white p-6">
      <div className="flex items-center gap-3"><span className="grid size-11 place-items-center rounded-2xl bg-clay/10 text-clay-dark"><ShoppingBag size={20} /></span><div><h2 className="font-display text-2xl font-bold">Top products this month</h2><p className="text-sm text-ink/45">Ranked by paid sales.</p></div></div>
      {topProducts.length ? <TopProductsPie products={topProducts} /> : <p className="mt-6 rounded-2xl bg-paper/60 p-6 text-center text-sm font-bold text-ink/45">No paid sales yet this month.</p>}
    </div>
  </>;
}

function greeting() {
  const hour = new Date().getHours(); return hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
}
